import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Loader, AlertShort } from '@sdir/sds';
import styled from 'styled-components';
import { ErrorMessage, SortableDataTable } from '@sdir/utilities/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { formatDateString } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
// eslint-disable-next-line max-len
import { HealthServiceCoreFeaturesMedicalExaminationDataAccessObjectsFitnessDecision as DecisionDto } from '@sdir/httpclient/lib/clients/aps/health';
import { medicalExaminationApi } from '../../../httpclient';
import { useCurrentPerson, useFeatureToggles } from '../../../services/hooks';
import { ToggleHealth } from '../../../constants/permissions';
import { HealthRow, HealthTableHeader } from '../../../helpers/TableHeaders';
import LimitationsSummary from './LimitationsSummary';
import EvaluationList from './EvaluationList';

const unfitTypes: DecisionDto[] = [
  DecisionDto.PermanentlyUnfit,
  DecisionDto.ProvisionallyUnfit,
  DecisionDto.TemporarilyUnfit,
];

const MyHealth: React.FC = () => {
  const intl = useIntl();
  const featureToggle = useFeatureToggles();

  const {
    person: { result: contactInfo },
  } = useCurrentPerson();

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const showHealth = !isToggleOn(featureToggle?.result, ToggleHealth, userPersonalNr);

  const { errorObject: labelsError, result: labelsResult } = useApi({
    apiFunction: () => medicalExaminationApi.v1MedicalcertificateGetmedicalexaminationlabelsGet(),
    callApiOnInit: true,
  });

  const { result, loading, error } = useApi({
    apiFunction: () =>
      medicalExaminationApi.v1MedicalcertificateQuerymedicalcertificatesOwnGet(
        true,
        undefined,
        true
      ),
    callApiOnInit: true,
  });

  const healthData: HealthRow[] = useMemo(() => {
    let items = result?.items || [];
    items = items.filter((h) => !unfitTypes.includes(h?.decision || DecisionDto.NotSet));
    items = items.filter((f) => f.isActive);
    return items.map((i) => {
      return {
        healthcheck: i?.decision
          ? intl.formatMessage({ id: `competence.table.health.decision.${i.decision}` })
          : '',
        validTo: i?.expiryDate ? formatDateString(i.expiryDate) : '',
        colorVisionDate: i?.examination?.colorVisionTestDate
          ? formatDateString(i.examination.colorVisionTestDate)
          : '',
        childContent: (
          <ChildContent>
            {i.examination && <EvaluationList labels={labelsResult} examination={i.examination} />}
            {i.examination && <LimitationsSummary examination={i.examination} />}
          </ChildContent>
        ),
      };
    });
  }, [result?.items]);

  const showEmpty = !healthData || healthData.length === 0;

  if (!showHealth) {
    return <></>;
  }

  return (
    <Wrapper>
      <h2>{intl.formatMessage({ id: 'home.tile.health' })}</h2>
      {loading && <Loader />}
      {error && <ErrorMessage error={error} />}
      {labelsError && <ErrorMessage error={labelsError} />}
      {showEmpty && !error && (
        <AlertShort text={intl.formatMessage({ id: 'health.notfound' })} type="info" />
      )}
      {result && !error && !showEmpty && (
        <TableWrapper data-testid="assessment-table">
          <StyledSortableDataTable
            autoWidth
            collapsable
            collapsableText={[
              intl.formatMessage({ id: 'general.table.viewdetails' }),
              intl.formatMessage({ id: 'general.table.hidedetails' }),
            ]}
            columns={HealthTableHeader(intl.formatMessage)}
            data={healthData}
            sortableKeys={['healthcheck', 'validTo', 'colorVisionDate']}
          />
        </TableWrapper>
      )}
    </Wrapper>
  );
};

export default MyHealth;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const ChildContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledSortableDataTable = styled(SortableDataTable)`
  margin-left: -5px;
  padding: 0 0 20px 0;
  background-color: transparent;
  thead tr th {
    padding-left: 0;
    button {
      text-align: left;
    }
  }
  & table > tbody > tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }

    & > td:last-child {
      text-align: right;
    }
  }
`;
